// ─────────────────────────────────────────────────────────────────────────────
// TrendSparkline, the Trending detail graph. (trend-viz 2026-07-06)
//
// Dependency-free inline SVG sparkline for a Trending item's `series` (the
// up-to-14 daily values the worker's sweep captured AT DETECTION TIME, the
// exact numbers its spike math ran on, spike day last). Rendered by BOTH the
// desktop detail modal (dashboard-sections/Trending.tsx) and the mobile
// bottom sheet (MobileTrendingCard.tsx) so the story reads identically.
//
// Look: smooth-ish quadratic-midpoint polyline, crimson (#8B1A2B) stroke over
// a soft same-crimson fill; the FINAL (spike) point gets a dot + halo and its
// value labeled ("1,322"); first/last dates sit under the baseline in the
// JetBrains mono. ~72px tall, full container width (measured via a ref +
// ResizeObserver, no preserveAspectRatio stretching, so strokes and text
// stay crisp at any width). Dates format LOCAL with the 'T12:00:00' noon
// anchor (the app's 12-hour rule, never UTC-shifted off by a day).
//
// GRACEFUL ABSENCE: items detected before series capture shipped have no
// `series`; anything under 2 points returns null and the detail views simply
// show no graph. Purely decorative → aria-hidden.
// ─────────────────────────────────────────────────────────────────────────────

import { useLayoutEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

const CRIMSON = '#8B1A2B';
const MONO = "'JetBrains Mono', ui-monospace, monospace";

/** Structural match for lib/trending's TrendingSeriesPoint (kept local so the
    design-system layer stays import-free of feature libs). */
export interface TrendSparklinePoint {
  readonly d: string;
  readonly v: number;
}

export interface TrendSparklineProps {
  readonly series?: ReadonlyArray<TrendSparklinePoint>;
  /** Pixel height of the SVG. Default 72 (Ryan's ~64-80px band). */
  readonly height?: number;
}

/** "2026-07-05" → "Jul 5", local-noon anchored (12-hour rule). */
function sparkDateLabel(d: string): string {
  const dt = new Date(String(d || '') + 'T12:00:00');
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Quadratic-midpoint smoothing, passes through the endpoints, eases the
    middle. "Smooth-ish": the last leg runs straight into the spike dot. */
function sparkPath(xs: ReadonlyArray<number>, ys: ReadonlyArray<number>): string {
  const n = xs.length;
  let path = 'M ' + xs[0].toFixed(1) + ' ' + ys[0].toFixed(1);
  for (let i = 1; i < n - 1; i++) {
    const mx = (xs[i] + xs[i + 1]) / 2;
    const my = (ys[i] + ys[i + 1]) / 2;
    path += ' Q ' + xs[i].toFixed(1) + ' ' + ys[i].toFixed(1) + ' ' + mx.toFixed(1) + ' ' + my.toFixed(1);
  }
  path += ' L ' + xs[n - 1].toFixed(1) + ' ' + ys[n - 1].toFixed(1);
  return path;
}

export function TrendSparkline({ series, height = 72 }: TrendSparklineProps): JSX.Element | null {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  // Measure the container so coordinates are real pixels (crisp at any width).
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const measure = (): void => setWidth(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Graceful absence: no/short/garbage series → no graph at all.
  const pts = (series || []).filter(
    (p) => !!p && typeof p.d === 'string' && isFinite(Number(p.v)),
  );
  if (pts.length < 2) return null;

  const values = pts.map((p) => Number(p.v));
  const vMin = Math.min(...values);
  const vMax = Math.max(...values);
  const vSpan = vMax - vMin || 1; // flat series still draws (a level line)

  // Geometry, top pad leaves room for the spike-value label, bottom pad for
  // the first/last date row.
  const padTop = 16;
  const padBottom = 18;
  const padX = 6;
  const plotBottom = height - padBottom;
  const plotH = plotBottom - padTop;
  const plotW = Math.max(width - padX * 2, 1);

  const xs = pts.map((_, i) => padX + (i / (pts.length - 1)) * plotW);
  const ys = values.map((v) => plotBottom - ((v - vMin) / vSpan) * plotH);
  const line = sparkPath(xs, ys);
  const lastX = xs[xs.length - 1];
  const lastY = ys[ys.length - 1];
  const fill =
    line +
    ' L ' + lastX.toFixed(1) + ' ' + plotBottom.toFixed(1) +
    ' L ' + xs[0].toFixed(1) + ' ' + plotBottom.toFixed(1) + ' Z';

  return (
    <div ref={hostRef} style={{ width: '100%' }} aria-hidden="true">
      {width > 40 ? (
        <svg
          width={width}
          height={height}
          viewBox={'0 0 ' + width + ' ' + height}
          style={{ display: 'block' }}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* soft crimson wash under the line */}
          <path d={fill} fill={CRIMSON} fillOpacity={0.12} stroke="none" />
          <path
            d={line}
            fill="none"
            stroke={CRIMSON}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* the spike day, halo + dot + its value ("1,322") */}
          <circle cx={lastX} cy={lastY} r={7} fill={CRIMSON} fillOpacity={0.16} />
          <circle cx={lastX} cy={lastY} r={3.2} fill={CRIMSON} />
          <text
            x={lastX - 2}
            y={Math.max(11, lastY - 8)}
            textAnchor="end"
            fontFamily={MONO}
            fontSize={11}
            fontWeight={700}
            fill={CRIMSON}
          >
            {Math.round(values[values.length - 1]).toLocaleString('en-US')}
          </text>
          {/* first / last dates, mono, muted, inherits the surface's text tone */}
          <text
            x={padX}
            y={height - 4}
            textAnchor="start"
            fontFamily={MONO}
            fontSize={10}
            fill="currentColor"
            opacity={0.55}
          >
            {sparkDateLabel(pts[0].d)}
          </text>
          <text
            x={width - padX}
            y={height - 4}
            textAnchor="end"
            fontFamily={MONO}
            fontSize={10}
            fill="currentColor"
            opacity={0.55}
          >
            {sparkDateLabel(pts[pts.length - 1].d)}
          </text>
        </svg>
      ) : null}
    </div>
  );
}
